import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MessageBox } from '@schuettflix/react-components';
import { Button } from '@schuettflix/planum-react';
import { SelectSellerProductInput } from '@/shared/inputs/SelectSellerProductInput.tsx';
import { generateLocationSources } from '@/modules/location/domain/generateLocationSources.ts';
import { useAutomaticFormContext } from '../context/AutomaticFormContext.tsx';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { derivePricingStrategy } from '../utils/derivePricingStrategy.ts';
import { LocationInput } from '@/shared/inputs/LocationInput.tsx';
import { WeighingLocationSelectionInput } from '@/shared/inputs/WeighingLocationSelectionInput.tsx';
import {
    isMissingLocationWithWeighingData,
    isWeighingLocationRequired,
    isWeighingLocationStale,
} from '../domain/isWeighingLocationRequired.ts';
import { Fieldset, FieldsetBody, FieldsetColumn } from '../components/Fieldset.tsx';
import { useFormClear } from '@/shared/hooks/useFormClear.ts';
import { getSellerProductPurchasePrice } from '../domain/getSellerProductPurchasePrice.ts';

export function LocationsFieldset() {
    const { t } = useTranslation();

    const methods = useFormContext<AutomaticProductsFormValues>();

    const { watch, clearField, getValues, register, setValue, clearErrors } = useFormClear(methods);
    const { constructionProjectId, customerOrganization } = useAutomaticFormContext();
    const customerOrganizationId = customerOrganization.id;

    const [
        productType,
        isSalesTransportIncluded,
        isPurchaseTransportIncluded,
        isStandalone,
        supplierOrganizationId,
        carrierOrganizationId,
        salesMaterialUnit,
        salesTransportUnit,
        purchaseMaterialUnit,
        purchaseTransportUnit,
        additionalServices,
        loadingLocation,
        unloadingLocation,
        freightType,
        sellerProduct,
        weighingLocation,
    ] = watch([
        'productType',
        'isSalesTransportIncluded',
        'isPurchaseTransportIncluded',
        'isStandalone',
        'supplierOrganizationId',
        'carrierOrganizationId',
        'salesMaterialUnit',
        'salesTransportUnit',
        'purchaseMaterialUnit',
        'purchaseTransportUnit',
        'additionalServices',
        'loadingLocation',
        'unloadingLocation',
        'freightType',
        'sellerProduct',
        'weighingLocation',
    ]);

    const businessCase = useMemo(
        () => ({
            productType,
            pricingStrategy: derivePricingStrategy(isSalesTransportIncluded, isPurchaseTransportIncluded),
            isStandalone,
        }),
        [isPurchaseTransportIncluded, isSalesTransportIncluded, isStandalone, productType]
    );

    const loadingSources = useMemo(
        () =>
            generateLocationSources({
                businessCase,
                constructionProjectId,
                supplierOrganizationId,
                carrierOrganizationId,
                customerOrganizationId,
                locationType: 'LOADING',
                freightType,
            }),
        [
            businessCase,
            constructionProjectId,
            supplierOrganizationId,
            carrierOrganizationId,
            customerOrganizationId,
            freightType,
        ]
    );

    const unloadingSources = useMemo(
        () =>
            generateLocationSources({
                businessCase,
                constructionProjectId,
                supplierOrganizationId,
                carrierOrganizationId,
                customerOrganizationId,
                locationType: 'UNLOADING',
                freightType,
            }),
        [
            businessCase,
            constructionProjectId,
            supplierOrganizationId,
            carrierOrganizationId,
            customerOrganizationId,
            freightType,
        ]
    );

    const weighingLocationRequirementValues = {
        productType,
        isSalesTransportIncluded,
        isPurchaseTransportIncluded,
        salesMaterialUnit,
        salesTransportUnit,
        purchaseMaterialUnit,
        purchaseTransportUnit,
        additionalServices,
        loadingLocation,
        unloadingLocation,
    };
    const showWeighingLocation = isWeighingLocationRequired(weighingLocationRequirementValues);
    const showMissingWeighingDataLocationError = isMissingLocationWithWeighingData(weighingLocationRequirementValues);

    const showSellerProduct = businessCase.pricingStrategy === 'FRANCO' || businessCase.pricingStrategy === 'MERCHANT';

    return (
        <Fieldset title={t('forms.automaticProductsForm.sections.route')} dataTest="locations-fieldset">
            <FieldsetBody className="grid-cols-8">
                <FieldsetColumn className="col-span-4" label={t('forms.automaticProductsForm.labels.loadingPoint')}>
                    <LocationInput
                        {...register('loadingLocation')}
                        locationType="LOADING"
                        label={t('forms.inputs.locationInput.selectLoadingLocation')}
                        locationSources={loadingSources.locationSources}
                        defaultOrganizationId={loadingSources.defaultOrganizationId}
                        onValueChange={loadingLocation => {
                            const [unloadingLocation, weighingLocation] = getValues([
                                'unloadingLocation',
                                'weighingLocation',
                            ]);

                            if (isWeighingLocationStale({ weighingLocation, loadingLocation, unloadingLocation })) {
                                clearField('weighingLocation');
                            }

                            if (loadingLocation?.location.legacyFactoryId !== sellerProduct?.legacyFactoryId) {
                                clearField('sellerProduct');
                            }
                        }}
                    />
                </FieldsetColumn>

                <FieldsetColumn className="col-span-4" label={t('forms.automaticProductsForm.labels.unloadingPoint')}>
                    <LocationInput
                        {...register('unloadingLocation')}
                        locationType="UNLOADING"
                        label={t('forms.inputs.locationInput.selectUnloadingLocation')}
                        locationSources={unloadingSources.locationSources}
                        defaultOrganizationId={unloadingSources.defaultOrganizationId}
                        onValueChange={unloadingLocation => {
                            const [loadingLocation, weighingLocation] = getValues([
                                'loadingLocation',
                                'weighingLocation',
                            ]);

                            if (isWeighingLocationStale({ weighingLocation, loadingLocation, unloadingLocation })) {
                                clearField('weighingLocation');
                            }
                        }}
                    />
                </FieldsetColumn>

                {showSellerProduct && (
                    <>
                        <FieldsetColumn className="col-span-4" label={t('forms.inputs.sellerProductInput.label')}>
                            <SelectSellerProductInput
                                legacyFactoryId={loadingLocation?.location.legacyFactoryId ?? undefined}
                                disabled={!loadingLocation?.location.legacyFactoryId}
                                label=""
                                dataTest="seller-product-select-input"
                                {...register('sellerProduct')}
                                onValueChange={(_sellerProduct, catalogueProduct) => {
                                    const purchasePrice = getSellerProductPurchasePrice({
                                        price: catalogueProduct?.price,
                                        purchaseMaterialUnit: getValues('purchaseMaterialUnit'),
                                    });

                                    if (purchasePrice !== undefined) {
                                        setValue('purchaseMaterialPrice', purchasePrice, { shouldDirty: true });
                                        clearErrors('purchaseMaterialPrice');
                                    }
                                }}
                            />
                        </FieldsetColumn>

                        <FieldsetColumn className="col-span-4" label=" ">
                            <Button
                                type="button"
                                disabled={!loadingLocation?.location.legacyFactoryId || !sellerProduct}
                                size="lg"
                                className="w-full"
                                onClick={() => {
                                    if (sellerProduct) {
                                        setValue('productName', sellerProduct.name);
                                        clearErrors('productName');
                                    }
                                }}
                            >
                                {t('forms.inputs.sellerProductInput.applySellerProductName')}
                            </Button>
                        </FieldsetColumn>
                    </>
                )}

                {showWeighingLocation && (
                    <FieldsetColumn
                        className="col-span-8 mt-2"
                        label={t('forms.inputs.WeighingLocationSelectionInput.label')}
                    >
                        {!weighingLocation && (
                            <MessageBox type="warning" size="md">
                                {t('forms.francoForm.sections.locations.choseWeighingDataLocation')}
                            </MessageBox>
                        )}
                        <WeighingLocationSelectionInput {...register('weighingLocation')} />
                    </FieldsetColumn>
                )}

                {showMissingWeighingDataLocationError && (
                    <FieldsetColumn className="col-span-8 mt-2" label=" ">
                        <MessageBox type="error" size="md" data-test="missing-weighing-location-error">
                            {t('forms.merchantForm.sections.locations.addWeighingData')}
                        </MessageBox>
                    </FieldsetColumn>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
